"use client";

import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Rocket, Globe2, Satellite } from "lucide-react";

// --- CUSTOM HOOKS ---
function useRevealOnScroll({
  threshold = 0.1
} = {}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold
    });
    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }
    return () => {
      if (currentRef) {
        observer.disconnect();
      }
    };
  }, [threshold]);
  return {
    ref,
    isVisible
  };
}

// --- DATA ---
const milestones = [{
  year: "2017",
  title: "Հիմնադրում",
  desc: "\xA02017թ. նոյեմբերի 16-ին՝"
}, {
  year: "2021",
  title: "2021թ. հոկտեմբերի 21-ին",
  desc: "Ստացել է տիեզերական գործունեություն իրականացնելու համար Հայաստանում առաջին լիցենզիան:"
}, {
  year: "2022",
  title: "2022թ. մայիսին",
  desc: "ՀՀ առաջին արբանյակի արձակում ուղեծիր"
}, {
  year: "2024",
  title: "2024թ. մայիսին",
  desc: "Կառավարման կենտրոնի հիմնում"
}, {
  year: "2025",
  title: "2025",
  desc: "Միջազգային Գործընկերների հետ կապերի հաստատում և պայմանագրերի կնքում"
}, {
  year: "2026",
  title: "2026",
  desc: "Armsat-2\xA0 արբանյակի ձեռքբերման նախապատրաստություն\xA0"
}];

// --- COMPONENTS ---
function OurJourneyTimeline() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const scrollContainerRef = useRef(null);
  const handleScroll = direction => {
    if (scrollContainerRef.current) {
      const scrollAmount = 350;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth"
      });
    }
  };
  return (
    // Changed min-h-[80vh] to min-h-screen and removed border-y
    <section id="ourjourneytimeline" className="relative bg-[#05070f] text-slate-100 overflow-hidden min-h-screen flex flex-col justify-center !p-0">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none z-0" />

      <div ref={ref} className={`relative z-10 max-w-[85rem] mx-auto px-4 sm:px-8 w-full transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold text-center mb-16 tracking-tight drop-shadow-md text-white">
          Մեր զարգացման ուղին
        </h2>

        {/* Carousel Wrapper */}
        <div className="relative group flex items-center">
          
          {/* Left Arrow Button */}
          <button onClick={() => handleScroll("left")} className="absolute -left-5 md:-left-6 z-20 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-[#0b0e14] border border-[#23293b] text-slate-400 hover:text-[#8b98ff] transition-all hover:scale-105 hover:bg-[#131620] shadow-xl" aria-label="Scroll left">
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Main Timeline Container */}
          <div className="w-full rounded-2xl border border-[#1e2330] bg-[#0a0d14]/90 backdrop-blur-md shadow-2xl relative overflow-hidden">
            <div ref={scrollContainerRef} className="flex w-full overflow-x-auto hide-scrollbar space-x-6 md:space-x-10 px-8 md:px-16 py-12 relative snap-x snap-mandatory">
              
              {/* Connecting Horizontal Line */}
              <div className="absolute top-[116px] left-0 w-[200%] h-[1px] bg-[#2a304f] z-0" />

              {milestones.map((item, i) => <div key={i} data-index={i} className="relative flex flex-col items-center w-[280px] shrink-0 group snap-center">
                  {/* Year */}
                  <div className="h-8 flex items-center text-[#8b98ff] font-sans text-xl md:text-2xl font-bold mb-6 drop-shadow-[0_0_8px_rgba(139,152,255,0.4)] transition-all duration-300">
                    {item.year}
                  </div>

                  {/* Node / Bullet */}
                  <div className="w-7 h-7 rounded-full bg-[#0a0d14] border-2 border-[#8b98ff] z-10 flex items-center justify-center mb-8 ring-[6px] ring-[#0a0d14]">
                    <div className="w-2 h-2 bg-[#8b98ff] rounded-full shadow-[0_0_8px_rgba(139,152,255,0.8)]" />
                  </div>

                  {/* Content Card */}
                  <div className="text-center bg-[#131620] p-6 md:p-8 rounded-xl border border-white/5 shadow-lg transition-all duration-300 hover:border-[#8b98ff]/30 w-full min-h-[160px] flex flex-col justify-start">
                    <h3 className="font-sans font-bold mb-4 text-slate-100 !text-xl">
                      {item.title}
                    </h3>
                    <p className="text-[#a0a5b5] whitespace-normal leading-relaxed !text-xl">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
              
              {/* Spacer at the end for proper scrolling overflow */}
              <div className="w-8 shrink-0"></div>
            </div>
          </div>

          {/* Right Arrow Button */}
          <button onClick={() => handleScroll("right")} className="absolute -right-5 md:-right-6 z-20 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-[#0b0e14] border border-[#23293b] text-slate-400 hover:text-[#8b98ff] transition-all hover:scale-105 hover:bg-[#131620] shadow-xl" aria-label="Scroll right">
            <ChevronRight className="w-5 h-5" />
          </button>
          
        </div>
      </div>
    </section>
  );
}

// --- MAIN APPLICATION COMPONENT ---
export default function App() {
  return (
    // Changed background to bg-[#05070f] and removed !h-[700px]
    <div className="min-h-screen bg-[#05070f] text-slate-50 font-sans selection:bg-indigo-500/30">
      {/* Global CSS to hide scrollbars but keep functionality */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `
      }} />

      {/* Header */}
      
      {/* Hero Section */}
      
      {/* The Requested Timeline Section */}
      <OurJourneyTimeline />

      {/* Footer */}
      
    </div>
  );
}